import { useEffect, useState } from 'react'
import {
  TimepointContainerSubmissionExamMetadata,
  TimepointContainerSubmissionExamMetadataResponse,
  TimepointContainerSubmissionStatus,
  TimepointSubmissionStatus,
  client,
} from '@common/config/api/client'
import { FileDownloadRounded, RemoveRedEyeRounded } from '@mui/icons-material'
import { Checkbox, IconButton, Stack, Table, TableBody, TableCell, TableContainer, TableRow } from '@mui/material'
import SeriesTableHeader from './SeriesTableHeader'
import SeriesTableBulk from './SeriesTableBulk'
import { SPACING } from '@common/theme/spacing'
import { SIZING } from '@common/theme/sizing'
import DeleteModal from '../modals/DeleteModal'
import { launchDicomViewerForQC, launchDicomViewerForSite } from '@common/utils/ohifUtils'
import { useParams } from 'react-router-dom'
import { useExtendedAuth } from '@auth/hooks/useExtendedAuth'
import Toast from '@common/components/Toast/Toast'
import { toast } from 'sonner'
import { useDownloadExamMetadata } from '@features/subject/hooks/useDownloadExamMetadata'
import { useDownloadExamMetadataMutation } from '@features/qc_task_details_view/hooks/useDownloadExamMetadataMutation'

const iconSx = {
  color: 'text.secondary',
  width: SIZING.iconWidth,
  height: SIZING.iconWidth,
} as const

async function downloadFromUrl(fileUrl: string, authenticated: boolean) {
  const blob = authenticated
    ? (await client.instance.get(fileUrl, { responseType: 'blob' })).data
    : await (await fetch(fileUrl)).blob()
  const blobUrl = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = blobUrl
  link.download = fileUrl.split('/').pop()?.split('?').shift() || 'series.zip'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(blobUrl)
}

export default function SeriesTable({
  container_submission_id,
  exam,
  timepointSubmissionStatus,
  timepointSubmissionId: timepointSubmissionIdProp,
}: {
  container_submission_id?: string
  exam?: TimepointContainerSubmissionExamMetadataResponse | TimepointContainerSubmissionExamMetadata
  timepointSubmissionStatus?: TimepointSubmissionStatus | TimepointContainerSubmissionStatus
  /** QC page passes this; site uses the route param */
  timepointSubmissionId?: string
}) {
  const [selected, setSelected] = useState<number[]>([])
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [downloading, setDownloading] = useState(false)
  const { qcTaskId, studyId, subjectId, timepointSubmissionId: timepointSubmissionIdFromParams } = useParams()
  const timepointSubmissionId = timepointSubmissionIdProp || timepointSubmissionIdFromParams
  const { user } = useExtendedAuth()
  const downloadExamMetadata = useDownloadExamMetadata()
  const { mutateAsync: downloadExamMetadataForQC } = useDownloadExamMetadataMutation()

  const isSelected = (index: number) => selected.indexOf(index) !== -1

  const handleSelectOne = (index: number) => {
    const selectedIndex = selected.indexOf(index)
    let newSelected: number[] = []
    let newSelectedIds: string[] = []

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, index)
      newSelectedIds = newSelectedIds.concat(selectedIds, exam?.series[index].series_metadata_id ?? '')
    } else {
      newSelected = selected.filter((i) => i !== index)
      newSelectedIds = selectedIds.filter((i) => i !== exam?.series[index].series_metadata_id)
    }

    setSelected(newSelected)
    setSelectedIds(newSelectedIds)
  }

  const handleSelectAll = () => {
    if (!exam?.series) return
    if (selected.length < exam.series.length) {
      setSelected(exam.series.map((_, idx) => idx))
      setSelectedIds(exam.series.map((series) => series.series_metadata_id!))
    } else {
      setSelected([])
      setSelectedIds([])
    }
  }

  const allSeries = exam?.series ?? []

  useEffect(() => {
    setSelected([])
    setSelectedIds([])
  }, [exam])

  const openSeriesInViewer = (seriesUid?: string | null) => {
    if (qcTaskId) {
      if (!studyId || !user?.access_token) {
        toast(
          <Toast
            message="Missing required parameters for QC task. Please ensure study ID and authentication token are provided."
            variant="error"
          />,
        )
        return
      }
      launchDicomViewerForQC(studyId, qcTaskId, user.access_token, seriesUid || undefined, true, true)
      return
    }
    if (!studyId || !subjectId || !timepointSubmissionId || !container_submission_id || !user?.access_token) {
      toast(
        <Toast
          message="Missing required parameters for site view. Please ensure all required parameters are provided."
          variant="error"
        />,
      )
      return
    }
    launchDicomViewerForSite(
      studyId,
      subjectId,
      timepointSubmissionId,
      container_submission_id,
      user.access_token,
      seriesUid || undefined,
      true,
    )
  }

  const handleDownloadSeries = async (seriesUid: string) => {
    if (!studyId || !container_submission_id || !timepointSubmissionId || downloading) return

    setDownloading(true)
    toast.loading('Preparing series download...', { id: 'series-download' })
    try {
      let url: string | undefined
      if (qcTaskId) {
        const response = await downloadExamMetadataForQC({
          path: { qc_task_id: qcTaskId },
          query: {
            study_id: studyId,
            timepoint_container_submission_id: container_submission_id,
            timepoint_submission_id: timepointSubmissionId,
            series_uid: seriesUid,
          },
        })
        url = response.url ?? undefined
        if (!url) throw new Error('Failed to fetch file')
        await downloadFromUrl(url, true)
      } else {
        if (!subjectId) throw new Error('Missing required parameters for site series download')
        const response = await downloadExamMetadata.mutateAsync({
          study_id: studyId,
          subject_id: subjectId,
          timepoint_submission_id: timepointSubmissionId,
          container_submission_id,
          series_uid: seriesUid,
          ...(exam?.study_uid && { dicom_study_uid: exam.study_uid }),
        })
        url = response.url ?? undefined
        if (!url) throw new Error('Failed to fetch file')
        await downloadFromUrl(url, false)
      }
      toast.success('Download started successfully', { id: 'series-download' })
    } catch (error) {
      toast.error(`Error downloading the series: ${error}`, { id: 'series-download' })
      console.error('Error downloading series:', error)
    } finally {
      setDownloading(false)
    }
  }

  const canDelete = timepointSubmissionStatus !== 'LOCKED' && timepointSubmissionStatus !== 'COMPLETE'

  return (
    <TableContainer
      sx={{
        borderRadius: SPACING.borderRadiusXl,
      }}
    >
      <Stack spacing={SPACING.minSpacing}>
        <SeriesTableBulk container_submission_id={container_submission_id} exam={exam} selectedIds={selectedIds} />
        <Table size="small">
          <SeriesTableHeader
            selected={selected}
            allSeries={allSeries}
            handleSelectAll={handleSelectAll}
            timepointSubmissionStatus={timepointSubmissionStatus}
          />
          <TableBody>
            {allSeries.map((series, index) => {
              const checked = isSelected(index)
              return (
                <TableRow key={index}>
                  {canDelete && (
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={checked}
                        onChange={() => handleSelectOne(index)}
                        sx={{
                          color: 'text.secondary',
                        }}
                      />
                    </TableCell>
                  )}
                  <TableCell>{series.description}</TableCell>
                  <TableCell>{series.series_number}</TableCell>
                  <TableCell>{series.acquisition_date}</TableCell>
                  <TableCell>{series.total_instances}</TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={SPACING.minSpacing} justifyContent="flex-end" alignItems="center">
                      <IconButton onClick={() => openSeriesInViewer(series.series_uid)}>
                        <RemoveRedEyeRounded sx={iconSx} />
                      </IconButton>
                      <IconButton
                        disabled={!series.series_uid || downloading}
                        onClick={() => series.series_uid && void handleDownloadSeries(series.series_uid)}
                        aria-label="Download series"
                      >
                        <FileDownloadRounded sx={iconSx} />
                      </IconButton>
                      {canDelete && series.series_metadata_id && (
                        <DeleteModal
                          container_submission_id={container_submission_id}
                          exam={exam}
                          selectedIds={[series.series_metadata_id]}
                        />
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </Stack>
    </TableContainer>
  )
}
